import { ZoneId } from '../types/zone';
import { ScholarSystemValue } from './scholarSystem';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

// prettier-ignore
const allCountryCodes = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 
  'AX', 'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 
  'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 
  'CH', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 
  'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 
  'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 
  'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM', 'HN', 'HR', 
  'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM', 
  'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 
  'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 
  'MF', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 
  'MV', 'MW', 'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP',
  'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 
  'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW', 'SA', 'SB', 'SC', 'SD', 
  'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 
  'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 
  'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 
  'VG', 'VI', 'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
] as const;

type CountryCode = (typeof allCountryCodes)[number];

type Country = {
  code: CountryCode;
  label: string;
  scholarSystem?: ScholarSystemValue;
  isPreferred?: boolean;
  defaultWorkingDays?: number[];
  defaultZoneId?: ZoneId;
};

const allCountries: {
  [K in CountryCode]: Omit<Country, 'label'> & { code: K };
} = {
  AD: { code: 'AD' },
  AE: { code: 'AE' },
  AF: { code: 'AF' },
  AG: { code: 'AG' },
  AI: { code: 'AI' },
  AL: { code: 'AL' },
  AM: { code: 'AM' },
  AO: { code: 'AO' },
  AQ: { code: 'AQ' },
  AR: { code: 'AR' },
  AS: { code: 'AS' },
  AT: { code: 'AT' },
  AU: {
    code: 'AU',
    isPreferred: true,
    scholarSystem: 'AU',
    defaultWorkingDays: [0, 1, 2, 3, 4],
    defaultZoneId: 'victoria',
  },
  AW: { code: 'AW' },
  AX: { code: 'AX' },
  AZ: { code: 'AZ' },
  BA: { code: 'BA' },
  BB: { code: 'BB' },
  BD: { code: 'BD' },
  BE: {
    code: 'BE',
    isPreferred: true,
    scholarSystem: 'BE',
    defaultZoneId: 'belgique-wallonie-bruxelles',
  },
  BF: { code: 'BF' },
  BG: { code: 'BG' },
  BH: { code: 'BH' },
  BI: { code: 'BI' },
  BJ: { code: 'BJ' },
  BL: { code: 'BL' },
  BM: { code: 'BM' },
  BN: { code: 'BN' },
  BO: { code: 'BO' },
  BQ: { code: 'BQ' },
  BR: { code: 'BR' },
  BS: { code: 'BS' },
  BT: { code: 'BT' },
  BV: { code: 'BV' },
  BW: { code: 'BW' },
  BY: { code: 'BY' },
  BZ: { code: 'BZ' },
  CA: { code: 'CA' },
  CC: { code: 'CC' },
  CD: { code: 'CD' },
  CF: { code: 'CF' },
  CG: { code: 'CG' },
  CH: { code: 'CH' },
  CI: { code: 'CI' },
  CK: { code: 'CK' },
  CL: { code: 'CL' },
  CM: { code: 'CM' },
  CN: { code: 'CN' },
  CO: { code: 'CO' },
  CR: { code: 'CR' },
  CU: { code: 'CU' },
  CV: { code: 'CV' },
  CW: { code: 'CW' },
  CX: { code: 'CX' },
  CY: { code: 'CY' },
  CZ: { code: 'CZ' },
  DE: { code: 'DE' },
  DJ: { code: 'DJ' },
  DK: { code: 'DK' },
  DM: { code: 'DM' },
  DO: { code: 'DO' },
  DZ: { code: 'DZ' },
  EC: { code: 'EC' },
  EE: { code: 'EE' },
  EG: { code: 'EG' },
  EH: { code: 'EH' },
  ER: { code: 'ER' },
  ES: { code: 'ES' },
  ET: { code: 'ET' },
  FI: { code: 'FI' },
  FJ: { code: 'FJ' },
  FK: { code: 'FK' },
  FM: { code: 'FM' },
  FO: { code: 'FO' },
  FR: {
    code: 'FR',
    isPreferred: true,
    scholarSystem: 'FR',
    defaultZoneId: 'zone-a',
  },
  GA: { code: 'GA' },
  GB: { code: 'GB' },
  GD: { code: 'GD' },
  GE: { code: 'GE' },
  GF: {
    code: 'GF',
    isPreferred: true,
    scholarSystem: 'FR',
    defaultZoneId: 'guyane',
  },
  GG: { code: 'GG' },
  GH: { code: 'GH' },
  GI: { code: 'GI' },
  GL: { code: 'GL' },
  GM: { code: 'GM' },
  GN: { code: 'GN' },
  GP: {
    code: 'GP',
    isPreferred: true,
    scholarSystem: 'FR',
    defaultZoneId: 'guadeloupe',
  },
  GQ: { code: 'GQ' },
  GR: { code: 'GR' },
  GS: { code: 'GS' },
  GT: { code: 'GT' },
  GU: { code: 'GU' },
  GW: { code: 'GW' },
  GY: { code: 'GY' },
  HK: { code: 'HK' },
  HM: { code: 'HM' },
  HN: { code: 'HN' },
  HR: { code: 'HR' },
  HT: { code: 'HT' },
  HU: { code: 'HU' },
  ID: { code: 'ID' },
  IE: { code: 'IE' },
  IL: { code: 'IL' },
  IM: { code: 'IM' },
  IN: { code: 'IN' },
  IO: { code: 'IO' },
  IQ: { code: 'IQ' },
  IR: { code: 'IR' },
  IS: { code: 'IS' },
  IT: { code: 'IT' },
  JE: { code: 'JE' },
  JM: { code: 'JM' },
  JO: { code: 'JO' },
  JP: { code: 'JP' },
  KE: { code: 'KE' },
  KG: { code: 'KG' },
  KH: { code: 'KH' },
  KI: { code: 'KI' },
  KM: { code: 'KM' },
  KN: { code: 'KN' },
  KP: { code: 'KP' },
  KR: { code: 'KR' },
  KW: { code: 'KW' },
  KY: { code: 'KY' },
  KZ: { code: 'KZ' },
  LA: { code: 'LA' },
  LB: { code: 'LB' },
  LC: { code: 'LC' },
  LI: { code: 'LI' },
  LK: { code: 'LK' },
  LR: { code: 'LR' },
  LS: { code: 'LS' },
  LT: { code: 'LT' },
  LU: { code: 'LU' },
  LV: { code: 'LV' },
  LY: { code: 'LY' },
  MA: { code: 'MA' },
  MC: { code: 'MC' },
  MD: { code: 'MD' },
  ME: { code: 'ME' },
  MF: { code: 'MF' },
  MG: { code: 'MG' },
  MH: { code: 'MH' },
  MK: { code: 'MK' },
  ML: { code: 'ML' },
  MM: { code: 'MM' },
  MN: { code: 'MN' },
  MO: { code: 'MO' },
  MP: { code: 'MP' },
  MQ: {
    code: 'MQ',
    isPreferred: true,
    scholarSystem: 'FR',
    defaultZoneId: 'martinique',
  },
  MR: { code: 'MR' },
  MS: { code: 'MS' },
  MT: { code: 'MT' },
  MU: { code: 'MU' },
  MV: { code: 'MV' },
  MW: { code: 'MW' },
  MX: { code: 'MX' },
  MY: { code: 'MY' },
  MZ: { code: 'MZ' },
  NA: { code: 'NA' },
  NC: {
    code: 'NC',
    isPreferred: true,
    scholarSystem: 'FR',
    defaultZoneId: 'nouvelle-caledonie',
  },
  NE: { code: 'NE' },
  NF: { code: 'NF' },
  NG: { code: 'NG' },
  NI: { code: 'NI' },
  NL: { code: 'NL' },
  NO: { code: 'NO' },
  NP: { code: 'NP' },
  NR: { code: 'NR' },
  NU: { code: 'NU' },
  NZ: { code: 'NZ' },
  OM: { code: 'OM' },
  PA: { code: 'PA' },
  PE: { code: 'PE' },
  PF: {
    code: 'PF',
    isPreferred: true,
    scholarSystem: 'FR',
    defaultZoneId: 'nouvelle-caledonie',
  },
  PG: { code: 'PG' },
  PH: { code: 'PH' },
  PK: { code: 'PK' },
  PL: { code: 'PL' },
  PM: {
    code: 'PM',
    scholarSystem: 'FR',
    defaultZoneId: 'saint-pierre-et-miquelon',
  },
  PN: { code: 'PN' },
  PR: { code: 'PR' },
  PS: { code: 'PS' },
  PT: { code: 'PT' },
  PW: { code: 'PW' },
  PY: { code: 'PY' },
  QA: { code: 'QA' },
  RE: {
    code: 'RE',
    isPreferred: true,
    scholarSystem: 'FR',
    defaultZoneId: 'reunion',
  },
  RO: { code: 'RO' },
  RS: { code: 'RS' },
  RU: { code: 'RU' },
  RW: { code: 'RW' },
  SA: { code: 'SA' },
  SB: { code: 'SB' },
  SC: { code: 'SC' },
  SD: { code: 'SD' },
  SE: { code: 'SE' },
  SG: { code: 'SG' },
  SH: { code: 'SH' },
  SI: { code: 'SI' },
  SJ: { code: 'SJ' },
  SK: { code: 'SK' },
  SL: { code: 'SL' },
  SM: { code: 'SM' },
  SN: { code: 'SN' },
  SO: { code: 'SO' },
  SR: { code: 'SR' },
  SS: { code: 'SS' },
  ST: { code: 'ST' },
  SV: { code: 'SV' },
  SX: { code: 'SX' },
  SY: { code: 'SY' },
  SZ: { code: 'SZ' },
  TC: { code: 'TC' },
  TD: { code: 'TD' },
  TF: { code: 'TF' },
  TG: { code: 'TG' },
  TH: { code: 'TH' },
  TJ: { code: 'TJ' },
  TK: { code: 'TK' },
  TL: { code: 'TL' },
  TM: { code: 'TM' },
  TN: { code: 'TN' },
  TO: { code: 'TO' },
  TR: { code: 'TR' },
  TT: { code: 'TT' },
  TV: { code: 'TV' },
  TW: { code: 'TW' },
  TZ: { code: 'TZ' },
  UA: { code: 'UA' },
  UG: { code: 'UG' },
  UM: { code: 'UM' },
  US: {
    code: 'US',
    isPreferred: true,
    scholarSystem: 'US',
    defaultZoneId: 'autre',
  },
  UY: { code: 'UY' },
  UZ: { code: 'UZ' },
  VA: { code: 'VA' },
  VC: { code: 'VC' },
  VE: { code: 'VE' },
  VG: { code: 'VG' },
  VI: { code: 'VI' },
  VN: { code: 'VN' },
  VU: { code: 'VU' },
  WF: { code: 'WF', scholarSystem: 'FR', defaultZoneId: 'wallis-et-futuna' },
  WS: { code: 'WS' },
  YE: { code: 'YE' },
  YT: {
    code: 'YT',
    isPreferred: true,
    scholarSystem: 'FR',
    defaultZoneId: 'mayotte',
  },
  ZA: { code: 'ZA' },
  ZM: { code: 'ZM' },
  ZW: { code: 'ZW' },
};

export function getDefaultZone(countryCode: string): ZoneId | undefined {
  return allCountries[countryCode]?.defaultZoneId;
}

export function getScholarSystem(
  countryCode: string,
): ScholarSystemValue | undefined {
  return allCountries[countryCode]?.scholarSystem;
}

export function useCountries() {
  const { t } = useTranslation(['countries']);

  return useMemo(() => {
    const sortedCountries: Country[] = Object.values(allCountries)
      .map(v => ({
        ...v,
        label: t(`countries:${v.code}`),
      }))
      .sort((a, b) => {
        if (a.isPreferred && !b.isPreferred) {
          return -1;
        }
        if (!a.isPreferred && b.isPreferred) {
          return 1;
        }
        return a.label.localeCompare(b.label);
      });

    return { sortedCountries };
  }, [t]);
}

export function getDefaultWorkingDays(countryCode?: string): number[] {
  if (!countryCode) {
    return [0, 1, 3, 4];
  }
  return allCountries[countryCode]?.defaultWorkingDays || [0, 1, 3, 4];
}
